import React, { useEffect, useState } from 'react';
import { Prompt } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { compose, bindActionCreators } from 'redux';
import { loadDocumentProperties, loadPropertyDetails, updateDocumentProperties } from '../actionCreators';
import { connect } from 'react-redux';
import { getDocumentProperties } from '../selectors';
import DocumentPropertiesCard from './DocumentPropertiesCard';
import { items, when, required } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation, isValid } from '@totalsoft/pure-validations-react';
import { field } from '@totalsoft/pure-validations/higherOrderValidators';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { getDynamicFieldControlType } from 'utils/functions';
import { ControlType } from '@totalsoft/rocket-ui';
import { format } from 'date-fns';

export const propertiesValidator = items(when(x => x.mandatory, required |> field('propValue')));

const DocumentPropertiesContainer = ({ documentId, siteId, actions, documentPropertiesData, documentPropertiesLoaded, t }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [localProperties, setLocalProperties] = useState([]);
  const [validation, validate, resetDirtyInfoProperties] = useDirtyFieldValidation(propertiesValidator);
  const [dirtyInfoProperties, setDirtyInfoProperties] = useDirtyInfo();
  const [isSaving, setIsSaving] = useState(false);
  const modelIsValid = validation |> isValid;

  useEffect(() => {
    if (!documentPropertiesLoaded) actions.loadDocumentProperties(documentId, siteId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (documentPropertiesLoaded) {
      setLocalProperties(documentPropertiesData);
    }
  }, [documentPropertiesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validate(localProperties, dirtyInfoProperties);
  }, [localProperties, dirtyInfoProperties, validate]);

  const onSaveProperties = async () => {
    if (!validate(localProperties)) {
      return;
    }
    setIsSaving(true);

    try {
      localProperties.forEach(prop => {
        if (getDynamicFieldControlType(prop.typeCode) !== ControlType.Date || !prop.propValue) return;
        prop.propValue = format(new Date(prop.propValue), 'yyyy-MM-dd').toString();
      });

      const event = await actions.updateDocumentProperties(documentId, siteId, localProperties);

      if (event.ok) {
        resetDirtyInfoProperties();
      }
    } finally {
      setIsSaving(false);
    }
    setIsDirty(false);
  };

  const onPropertyChange = (key, value) => {
    let property = localProperties.filter(x => x.documentPropertyId === Number(key))[0];
    const index = localProperties.indexOf(property);
    let propValue = value === 0 ? '0' : value;

    const newProperties = Object.assign([...localProperties], { [index]: { ...property, propValue } });
    setDirtyInfoProperties(`${index}.propValue`);

    setLocalProperties(newProperties);
    setIsDirty(true);
  };

  const loadPropertyDetails = propId => {
    const property = localProperties.filter(x => x.documentPropertyId === Number(propId))[0];
    return actions.loadPropertyDetails(property.propertyId);
  };

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <DocumentPropertiesCard
        properties={localProperties.map((obj, idx) => ({ ...obj, validation: validation[idx] }))}
        onPropertyChange={onPropertyChange}
        loadPropertyDetails={loadPropertyDetails}
        onSaveProperties={onSaveProperties}
        isDirty={isDirty}
        readOnly={isSaving}
        isValid={modelIsValid}
      />
    </>
  );
};

DocumentPropertiesContainer.propTypes = {
  t: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  documentPropertiesData: PropTypes.array.isRequired,
  documentPropertiesLoaded: PropTypes.bool.isRequired
};

function mapStateToProps(state, route) {
  const key = `${route.documentId}-${route.siteId}`;
  const { properties, loaded } = getDocumentProperties(state, key);
  return {
    documentPropertiesData: properties,
    documentPropertiesLoaded: loaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDocumentProperties,
        loadPropertyDetails,
        updateDocumentProperties
      },
      dispatch
    )
  };
}

export default compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(DocumentPropertiesContainer);
